/* eslint-disable no-underscore-dangle */
/* eslint-disable class-methods-use-this */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { CertificateValidationInterface } from "@ajsprivate/acrobatjs";
import { envUtil } from "dc-core";
import {
    VALIDATOR_NAME,
    VALIDATION_STATUS,
    VALIDATION_TIME,
    HTTP_ERROR_STRINGS,
} from "../../utils/constants";

class AdobeSignCertificateValidation extends CertificateValidationInterface {
    static isAvailable() {
        return true;
    }

    name() {
        return VALIDATOR_NAME.AdobeSignValidator;
    }

    static getValidationAPI() {
        let api = "https://certificatevalidator-dev.adobe.io";
        const apiKey = "certificatevalidator";

        const hostEnv = envUtil.getHostEnv();
        if (hostEnv === "prod") {
            api = "https://certificatevalidator.adobe.io";
        } else if (hostEnv === "stage") {
            api = "https://certificatevalidator-stage.adobe.io";
        }

        api += "/certificatevalidator/v1/verify";

        return { api, apiKey };
    }

    _getError(message, errorCode) {
        const error = new Error(message);
        error.reason = errorCode ? { code: errorCode } : {};
        return error;
    }

    validate(certificateChain) {
        // eslint-disable-next-line consistent-return
        return new Promise((resolve, reject) => {
            if (!Array.isArray(certificateChain)) {
                return reject(this._getError("certificateChain must be an array"));
            }

            const { api, apiKey } = AdobeSignCertificateValidation.getValidationAPI();

            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            headers.append("x-api-key", apiKey);

            const requestOptions = {
                method: "POST",
                headers,
                body: JSON.stringify({ certificates: certificateChain }),
            };

            fetch(api, requestOptions)
                // eslint-disable-next-line consistent-return
                .then(async response => {
                    if (response.ok) {
                        const resp = await response.json();
                        if (!resp.validation) {
                            resp.validation = {
                                validatedOn: VALIDATION_TIME.currentTime,
                                timestamp: new Date().toISOString(),
                            };
                        }

                        /*
                            In the API response, if trustSources array is empty and status is VALID,
                            the status should be UNKNOWN, currently the API returns VALID for cryptographically
                            correct certificate chains even if the certificate is not listed on any of the trust lists
                        */
                        if (resp && resp.status === VALIDATION_STATUS.valid && !resp.trustSources.length) {
                            resp.status = VALIDATION_STATUS.unknown;
                        }
                        resolve(resp);
                    }

                    const errorCode = response.status;
                    let errorMessage = HTTP_ERROR_STRINGS.unknownError;

                    if (errorCode === 429) {
                        errorMessage = HTTP_ERROR_STRINGS.tooManyRequests;
                    } else if (errorCode === 504) {
                        errorMessage = HTTP_ERROR_STRINGS.gatewayTimeout;
                    } else if (errorCode >= 500 && errorCode < 600) {
                        errorMessage = HTTP_ERROR_STRINGS.internalServerError;
                    }
                    throw this._getError(errorMessage, errorCode);
                })
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }
}

export default AdobeSignCertificateValidation;
